import { z } from 'zod'

import { occasionType } from './product.js'

/**
 * 회사 자주 쓰는 문구 (리본·카드) API 계약 (BIZ-48).
 *
 * 시스템 상용구(謹弔·祝 開業 등)는 코드 상수로 주문서 칩에 이미 제공된다. 이 계약은
 * 회사가 등록하는 커스텀 문구다. 유형별로 나뉘고, 근조·축하는 리본(30자·줄바꿈 불가),
 * 난·기타는 카드(100자). 관리=기업 관리자, 조회·선택=담당자.
 */

export const companyPhraseResponse = z.object({
  id: z.number().int(),
  occasionType,
  phrase: z.string(),
  displayOrder: z.number().int(),
})
export type CompanyPhraseResponse = z.infer<typeof companyPhraseResponse>

export const companyPhraseListResponse = z.object({
  items: z.array(companyPhraseResponse),
})
export type CompanyPhraseListResponse = z.infer<typeof companyPhraseListResponse>

/** 유형별 글자수·줄바꿈 규칙을 입력 단계에서 먼저 막는다(DB CHECK와 동일 규칙). */
function checkPhrase(occasion: z.infer<typeof occasionType>, phrase: string, ctx: z.RefinementCtx): void {
  const isCard = occasion === 'GIFT'
  const max = isCard ? 100 : 30
  if (phrase.length > max) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phrase'], message: isCard ? '카드 문구는 100자 이하여야 합니다' : '리본 문구는 30자 이하여야 합니다' })
  }
  if (!isCard && /[\n\r]/.test(phrase)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phrase'], message: '리본 문구에는 줄바꿈을 넣을 수 없습니다' })
  }
}

export const createCompanyPhraseRequest = z
  .object({
    occasionType,
    phrase: z.string().trim().min(1, '문구를 입력하세요'),
    displayOrder: z.number().int().optional(),
  })
  .superRefine((d, ctx) => checkPhrase(d.occasionType, d.phrase, ctx))
export type CreateCompanyPhraseRequest = z.infer<typeof createCompanyPhraseRequest>

/** 부분 수정. 유형은 바꾸지 않는다(문구별 고정). 길이 규칙은 도메인이 기존 유형으로 재검증한다. */
export const updateCompanyPhraseRequest = z
  .object({
    phrase: z.string().trim().min(1, '문구를 입력하세요'),
    displayOrder: z.number().int(),
  })
  .partial()
export type UpdateCompanyPhraseRequest = z.infer<typeof updateCompanyPhraseRequest>

export const CompanyPhraseErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  NOT_FOUND: 'COMPANY_PHRASE_NOT_FOUND',
  NO_COMPANY: 'NO_COMPANY',
  FORBIDDEN: 'FORBIDDEN',
} as const
export type CompanyPhraseErrorCode = (typeof CompanyPhraseErrorCode)[keyof typeof CompanyPhraseErrorCode]
